/**
 * The patient's anamnesis — the chart section, the protocol block and the
 * allergy mark on the plate (`docs/web-reference/patient-surfaces.md`).
 */
export const anamnesis = {
  'anamnesis.title': 'Anamnesis',
  'anamnesis.description': 'Life history and allergies, shared by the whole chart and kept as versions.',
  'anamnesis.loading': 'Loading anamnesis…',
  'anamnesis.loadFailed': 'The anamnesis could not be loaded.',
  'anamnesis.forbidden': 'Your role does not allow reading the anamnesis.',
  'anamnesis.notFound': 'This patient was not found.',
  'anamnesis.empty': 'No anamnesis recorded yet',
  'anamnesis.emptyDetail': 'Record the life history and the allergy statement once; every visit then reads it.',
  'anamnesis.edit': 'Edit',
  'anamnesis.protocolEdit': 'Change in the chart',
  'anamnesis.protocolSealedNone': 'No anamnesis is sealed with this signed protocol.',
  'anamnesis.record': 'Record anamnesis',
  'anamnesis.save': 'Save',
  'anamnesis.saving': 'Saving…',
  'anamnesis.cancel': 'Cancel',
  'anamnesis.saved': 'Saved as version {revision}.',
  'anamnesis.historyShow': 'Version history',
  'anamnesis.historyHide': 'Hide history',
  'anamnesis.historyTitle': 'Version history',
  'anamnesis.historyEmpty': 'No versions yet.',
  'anamnesis.historyFailed': 'The version history could not be loaded.',
  'anamnesis.historyMore': 'Show earlier versions',
  'anamnesis.version': 'Version {revision}',
  'anamnesis.recordedAt': 'Recorded {when}',
  'anamnesis.recordedBy': 'Recorded {when} by {who}',
  'anamnesis.allergiesTitle': 'Allergies and intolerances',
  'anamnesis.allergyStatus': 'Allergy status',
  'anamnesis.lifeHistoryTitle': 'Life history',
  'anamnesis.lifeHistoryEmpty': 'No life history recorded.',
  'anamnesis.entryKind': '{type} — {category}',
  'anamnesis.manifestations': 'Reactions',
  'anamnesis.listSeparator': ', ',
  'anamnesis.note': 'Note',
  'anamnesis.entryLegend': 'Allergy {number}',
  'anamnesis.entryType': 'Kind',
  'anamnesis.entryCategory': 'Category',
  'anamnesis.criticalityLabel': 'Risk',
  'anamnesis.criticalityNone': 'Not stated',
  'anamnesis.substance': 'Substance',
  'anamnesis.substanceBrowse': 'Choose in the dictionary',
  'anamnesis.dictionaryTitleDrug': 'Drug substances',
  'anamnesis.dictionaryTitleOther': 'Other allergens',
  'anamnesis.dictionaryTabCatalogue': 'Dictionary',
  'anamnesis.dictionarySearchPlaceholder': 'Substance name',
  'anamnesis.dictionaryHintClassified': 'Open an ATC group, or type at least two letters of the international nonproprietary name. Trade names are not in this dictionary.',
  'anamnesis.dictionaryHint': 'Type at least two letters of the name, or choose from the list.',
  'anamnesis.dictionaryUnclassified': 'No ATC group',
  'anamnesis.dictionaryOtherLetters': 'Other characters',
  'anamnesis.dictionaryMore': 'Show more',
  'anamnesis.dictionaryLoading': 'Loading the dictionary…',
  'anamnesis.dictionarySearching': 'Searching…',
  'anamnesis.dictionaryLoadFailed': 'The dictionary could not be loaded.',
  'anamnesis.dictionarySearchFailed': 'The dictionary could not be searched.',
  'anamnesis.dictionarySearchEmpty': 'Nothing in the dictionary matches.',
  'anamnesis.dictionaryEmptyLevel': 'This group is empty.',
  'anamnesis.dictionarySearchResults': 'Search results',
  'anamnesis.clearCoding': 'Remove the dictionary code',
  'anamnesis.reactionText': 'Reaction in your own words',
  'anamnesis.addReaction': 'Add reaction',
  'anamnesis.reactionSearch': 'Find a reaction in the dictionary',
  'anamnesis.removeReaction': 'Remove reaction {name}',
  'anamnesis.removeEntry': 'Remove allergy {number}',
  'anamnesis.addEntry': 'Add allergy',
  'anamnesis.habitSearch': 'Find a habit in the dictionary',
  'anamnesis.removeCode': 'Remove {name}',
  'anamnesis.problemNoEntries': 'List at least one allergy, or choose another status.',
  'anamnesis.problemNoSubstance': 'Every allergy needs a substance.',
  'anamnesis.changedConcurrently': 'The anamnesis changed while you were editing.',
  'anamnesis.codingInvalid': 'A dictionary code was not accepted. Choose it from the list again.',
  'anamnesis.codingUnbound': 'The clinic has no dictionary bound for this field. Remove the code and keep the text.',
  'anamnesis.writeSessionEnded': 'Your session has ended. Sign in again to save.',
  'anamnesis.writeForbidden': 'Your role does not allow changing the anamnesis.',
  'anamnesis.writeInvalid': 'The anamnesis was not accepted. Check the fields and try again.',
  'anamnesis.writeUnavailable': 'The server is unavailable. Your changes are kept in the form; try again.',
  'anamnesis.writeFailed': 'The anamnesis could not be saved. Try again.',
  'anamnesis.conflictTitle': 'Version {revision} was saved while you were editing.',
  'anamnesis.conflictDetail': 'Your changes are still here. Save them over the newer version, or discard them and read what was saved.',
  'anamnesis.conflictOverwrite': 'Save my version over it',
  'anamnesis.conflictDiscard': 'Discard my changes',
  'anamnesis.markTitle': 'Allergies:',
  'anamnesis.markOpen': 'Open the anamnesis',
  'anamnesis.markMore': '+{count} more',
  'anamnesis.section.pastIllnesses': 'Past illnesses',
  'anamnesis.section.surgeriesInjuries': 'Surgeries and injuries',
  'anamnesis.section.familyHistory': 'Family history',
  'anamnesis.section.livingConditions': 'Living and working conditions',
  'anamnesis.section.habits': 'Habits',
  'anamnesis.section.obstetricGynecological': 'Obstetric and gynaecological history',
  'anamnesis.section.transfusions': 'Blood transfusions',
  'anamnesis.section.medications': 'Regular medications',
  'anamnesis.section.other': 'Other',
  'anamnesis.status.recorded': 'Has allergies or intolerances',
  'anamnesis.status.noneKnown': 'No known allergies',
  'anamnesis.status.unknown': 'Not assessed',
  'anamnesis.type.allergy': 'Allergy',
  'anamnesis.type.intolerance': 'Intolerance',
  'anamnesis.category.medication': 'Medication',
  'anamnesis.category.food': 'Food',
  'anamnesis.category.insect': 'Insect',
  'anamnesis.category.other': 'Other',
  'anamnesis.criticality.low': 'Low risk',
  'anamnesis.criticality.high': 'High risk',
  'anamnesis.criticality.unableToAssess': 'Risk not assessed',
} as const
